import { useState, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { getAccounts } from '../services/accountService';
import { getErrorMessage } from '../utils/errorMessages';
import type { Account } from '../types/Account';

export function useAccounts() {
  const { oktaAuth, authState } = useOktaAuth();
  const getAccessTokenRef = useRef(oktaAuth.getAccessToken.bind(oktaAuth));
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAccessTokenRef.current = oktaAuth.getAccessToken.bind(oktaAuth);
  }, [oktaAuth]);

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      setAccounts([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function fetchAccounts() {
      setIsLoading(true);
      setError(null);

      try {
        const token = getAccessTokenRef.current();
        if (!token) {
          setAccounts([]);
          return;
        }

        const data = await getAccounts(token);
        if (!cancelled) {
          setAccounts(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAccounts([]);
          const status = (err as { status?: number }).status;
          const hasStatus = typeof status === 'number';
          setError(
            hasStatus ? getErrorMessage(status) : (err instanceof Error ? err.message : 'An unexpected error occurred'),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAccounts();

    return () => {
      cancelled = true;
    };
  }, [authState?.isAuthenticated]);

  return { accounts, isLoading, error };
}
